import { useCallback, useEffect, useState } from 'react';
import { ErrorHandler } from '../Services/UserApi';
import useHash from './UseHash';
import PhotoService from '../Services/PhotoService';

export interface Photo {
	clientUploadId: number;
	clientUploadFilename: string;
	clientUploadExtension: string;
	clientUploadTime: number;
	clientUploadSize: number;
	clientUploadCheckSum: string;
	clientId: number;
}

export default function usePhotos() {
	const [photos, setPhotos] = useState<Photo[]>();
	const [isLoading, setIsLoading] = useState(true);

	const hash = useHash();

	const loadPhotos = useCallback(async () => {
		setIsLoading(true);
		try {
			const result = await PhotoService.getPhotos(hash);
			setPhotos(result);
		} catch (error) {
			ErrorHandler.toast(error);
		} finally {
			setIsLoading(false);
		}
	}, [hash]);

	const deletePhoto = async (photoId: number) => {
		await PhotoService.deletePhoto(hash, photoId);

		// Remove photo from local state
		setPhotos((prev) => prev?.filter((p) => p.clientUploadId !== photoId));
	};

	const deleteMultiplePhotos = async (photoIds: number[]) => {
		const response = await PhotoService.deleteMultiplePhotos(hash, photoIds);

		// Remove photos from local state
		setPhotos((prev) => prev?.filter((p) => !photoIds.includes(p.clientUploadId)));

		return response;
	};

	const uploadPhoto = async (file: File) => {
		const response = await PhotoService.uploadPhoto(hash, file);

		// Add new photo to local state
		if (response.photo) {
			setPhotos((prev) => (prev ? [...prev, response.photo] : [response.photo]));
		}

		return response;
	};

	useEffect(() => {
		void loadPhotos();
	}, [loadPhotos]);

	return {
		photos,
		isLoading,
		loadPhotos,
		deletePhoto,
		deleteMultiplePhotos,
		uploadPhoto,
	};
}
